import { RRule, type Options, type Weekday } from 'rrule';

/**
 * Minimal interface for a recurring calendar slot.
 * Any object with these fields can be expanded — keeps the utility decoupled
 * from the full CalendarSlot model.
 */
export interface RecurringSlot {
  /** YYYY-MM-DD — serves as DTSTART */
  dateFrom: string;
  /** YYYY-MM-DD — optional end boundary for the recurrence */
  dateTo?: string | null;
  /** iCal RRULE string (e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR) */
  recurrenceRule: string;
  /** Exception dates to skip (YYYY-MM-DD) — stored in slot data */
  exdates?: string[];
}

// ── Internal helpers ────────────────────────────────────────────────────────

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function dateKeyToUtc(dateKey: string): Date {
  const m = DATE_KEY_RE.exec(dateKey);
  if (!m) throw new Error(`Invalid date key: ${dateKey}`);
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
}

function utcToDateKey(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ── Weekday mapping ─────────────────────────────────────────────────────────

const RRULE_DAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export type RRuleDay = (typeof RRULE_DAYS)[number];

/**
 * Convert ISO weekday (0 = Monday … 6 = Sunday) to an RRULE day abbreviation.
 */
export function isoWeekdayToRRuleDay(isoWeekday: number): RRuleDay {
  if (isoWeekday < 0 || isoWeekday > 6) {
    throw new RangeError(`isoWeekday must be 0–6, got ${isoWeekday}`);
  }
  return RRULE_DAYS[isoWeekday];
}

// ── Frequency mapping ───────────────────────────────────────────────────────

/** All RRULE frequencies, from most-coarse to most-fine. */
export const RRULE_FREQUENCIES = ['yearly', 'monthly', 'weekly', 'daily', 'hourly', 'minutely', 'secondly'] as const;
export type RRuleFrequency = (typeof RRULE_FREQUENCIES)[number];

const FREQUENCY_MAP: Record<RRuleFrequency, number> = {
  yearly: RRule.YEARLY,
  monthly: RRule.MONTHLY,
  weekly: RRule.WEEKLY,
  daily: RRule.DAILY,
  hourly: RRule.HOURLY,
  minutely: RRule.MINUTELY,
  secondly: RRule.SECONDLY,
};

const INT_TO_FREQUENCY: Record<number, RRuleFrequency> = {
  [RRule.YEARLY]: 'yearly',
  [RRule.MONTHLY]: 'monthly',
  [RRule.WEEKLY]: 'weekly',
  [RRule.DAILY]: 'daily',
  [RRule.HOURLY]: 'hourly',
  [RRule.MINUTELY]: 'minutely',
  [RRule.SECONDLY]: 'secondly',
};

const WEEKDAY_INT_TO_DAY: RRuleDay[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

// ── Positional weekday ──────────────────────────────────────────────────────

/**
 * A weekday in an RRULE BYDAY clause, optionally prefixed with a positional
 * number (e.g. `1MO` = first Monday, `-1FR` = last Friday of the period).
 */
export interface PositionalWeekday {
  day: RRuleDay;
  /** Optional positional prefix. Omit for a plain weekday entry. */
  n?: number;
}

// ── Full RRULE descriptor ───────────────────────────────────────────────────

/**
 * Structured representation of every RRULE field this project supports.
 * Canonical in-memory form used by the RruleEditor component.
 */
export interface RecurrenceRuleDescriptor {
  frequency: RRuleFrequency;
  /** `INTERVAL=` — omitted when 1. */
  interval?: number;
  /** `COUNT=` — mutually exclusive with `until`. */
  count?: number;
  /** `UNTIL=` — YYYY-MM-DD. Mutually exclusive with `count`. */
  until?: string;
  /** `BYDAY=` — weekdays with optional positional prefixes. */
  byDay?: PositionalWeekday[];
  /** `BYMONTHDAY=` — 1..31 or -1..-31. */
  byMonthDay?: number[];
  /** `BYMONTH=` — 1..12. */
  byMonth?: number[];
  /** `BYYEARDAY=` — 1..366 or -1..-366. */
  byYearDay?: number[];
  /** `BYWEEKNO=` — 1..53 or negative. */
  byWeekNo?: number[];
  /** `BYHOUR=` — 0..23. */
  byHour?: number[];
  /** `BYMINUTE=` — 0..59. */
  byMinute?: number[];
  /** `BYSECOND=` — 0..59. */
  bySecond?: number[];
  /** `BYSETPOS=` — filters the match list by ordinal position. */
  bySetPos?: number[];
  /** `WKST=` — week start day, MO by default. */
  wkst?: RRuleDay;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Parse an RRULE string into an `RRule` instance.
 * The string must NOT include the `RRULE:` prefix or `DTSTART`.
 *
 * @throws on invalid input
 */
export function parseRecurrenceRule(rule: string): RRule {
  const options = RRule.parseString(rule);
  return new RRule(options);
}

// ── Descriptor ↔ RRULE string ──────────────────────────────────────────────

const RRULE_FIELD_ORDER: Array<keyof RecurrenceRuleDescriptor> = [
  'frequency',
  'interval',
  'wkst',
  'count',
  'until',
  'byMonth',
  'byWeekNo',
  'byYearDay',
  'byMonthDay',
  'byDay',
  'byHour',
  'byMinute',
  'bySecond',
  'bySetPos',
];

const RRULE_FIELD_KEYS: Record<keyof RecurrenceRuleDescriptor, string> = {
  frequency: 'FREQ',
  interval: 'INTERVAL',
  count: 'COUNT',
  until: 'UNTIL',
  byDay: 'BYDAY',
  byMonthDay: 'BYMONTHDAY',
  byMonth: 'BYMONTH',
  byYearDay: 'BYYEARDAY',
  byWeekNo: 'BYWEEKNO',
  byHour: 'BYHOUR',
  byMinute: 'BYMINUTE',
  bySecond: 'BYSECOND',
  bySetPos: 'BYSETPOS',
  wkst: 'WKST',
};

const POSITIONAL_WEEKDAY_RE = /^([+-]?\d{1,2})?(MO|TU|WE|TH|FR|SA|SU)$/;

/**
 * Format a descriptor as an RFC 5545 RRULE string (without the `RRULE:` prefix).
 *
 * @throws when the descriptor is internally inconsistent (e.g. both count and until).
 */
export function formatRecurrenceDescriptor(desc: RecurrenceRuleDescriptor): string {
  if (!(desc.frequency in FREQUENCY_MAP)) {
    throw new Error(`Unknown frequency: ${desc.frequency}`);
  }
  if (desc.count !== undefined && desc.until !== undefined) {
    throw new Error('RRULE may specify COUNT or UNTIL, not both');
  }

  return RRULE_FIELD_ORDER.map(field => formatField(desc, field))
    .filter((part): part is string => part !== null)
    .join(';');
}

type FieldFormatter = (desc: RecurrenceRuleDescriptor, key: string) => string | null;

const formatFrequency: FieldFormatter = (desc, key) => `${key}=${desc.frequency.toUpperCase()}`;
const formatInterval: FieldFormatter = (desc, key) => (desc.interval && desc.interval > 1 ? `${key}=${desc.interval}` : null);
const formatWkst: FieldFormatter = (desc, key) => (desc.wkst && desc.wkst !== 'MO' ? `${key}=${desc.wkst}` : null);
const formatCount: FieldFormatter = (desc, key) => (desc.count !== undefined && desc.count > 0 ? `${key}=${desc.count}` : null);
const formatUntil: FieldFormatter = (desc, key) => (desc.until ? `${key}=${dateKeyToUntilString(desc.until)}` : null);
const formatByDay: FieldFormatter = (desc, key) =>
  desc.byDay?.length ? `${key}=${desc.byDay.map(formatPositionalWeekday).join(',')}` : null;
const formatNumericField =
  (field: keyof RecurrenceRuleDescriptor): FieldFormatter =>
  (desc, key) => {
    const value = desc[field] as number[] | undefined;
    return value?.length ? `${key}=${value.join(',')}` : null;
  };

const FIELD_FORMATTERS: Record<keyof RecurrenceRuleDescriptor, FieldFormatter> = {
  frequency: formatFrequency,
  interval: formatInterval,
  wkst: formatWkst,
  count: formatCount,
  until: formatUntil,
  byDay: formatByDay,
  byMonth: formatNumericField('byMonth'),
  byMonthDay: formatNumericField('byMonthDay'),
  byYearDay: formatNumericField('byYearDay'),
  byWeekNo: formatNumericField('byWeekNo'),
  byHour: formatNumericField('byHour'),
  byMinute: formatNumericField('byMinute'),
  bySecond: formatNumericField('bySecond'),
  bySetPos: formatNumericField('bySetPos'),
};

function formatField(desc: RecurrenceRuleDescriptor, field: keyof RecurrenceRuleDescriptor): string | null {
  return FIELD_FORMATTERS[field](desc, RRULE_FIELD_KEYS[field]);
}

const NUMERIC_FIELD_MAP: Array<[keyof Options, keyof RecurrenceRuleDescriptor]> = [
  ['bymonth', 'byMonth'],
  ['bymonthday', 'byMonthDay'],
  ['byyearday', 'byYearDay'],
  ['byweekno', 'byWeekNo'],
  ['byhour', 'byHour'],
  ['byminute', 'byMinute'],
  ['bysecond', 'bySecond'],
  ['bysetpos', 'bySetPos'],
];

/**
 * Parse an RRULE string into a {@link RecurrenceRuleDescriptor}.
 *
 * @throws on syntactically invalid RRULE.
 */
export function parseRecurrenceDescriptor(rule: string): RecurrenceRuleDescriptor {
  const options = RRule.parseString(rule);

  const frequency = options.freq !== undefined ? INT_TO_FREQUENCY[options.freq] : undefined;
  if (!frequency) {
    throw new Error(`RRULE is missing FREQ: ${rule}`);
  }

  const desc: RecurrenceRuleDescriptor = { frequency };

  if (options.interval && options.interval > 1) desc.interval = options.interval;
  if (options.count) desc.count = options.count;
  if (options.until) desc.until = utcToDateKey(options.until);

  const byDay = normalizeByDay(options.byweekday);
  if (byDay.length) desc.byDay = byDay;

  for (const [optionKey, descKey] of NUMERIC_FIELD_MAP) {
    const value = options[optionKey];
    if (value != null) (desc[descKey] as number[]) = toNumberArray(value as number | number[]);
  }

  const wkst = normalizeWkst(options.wkst);
  if (wkst) desc.wkst = wkst;

  return desc;
}

function normalizeWkst(wkst: Options['wkst'] | undefined): RRuleDay | null {
  if (wkst === null || wkst === undefined) return null;
  const index = typeof wkst === 'number' ? wkst : (wkst as Weekday).weekday;
  const day = WEEKDAY_INT_TO_DAY[index];
  return day && day !== 'MO' ? day : null;
}

/**
 * Validate an RRULE string. Returns `{ valid: true }` when it parses, or
 * `{ valid: false, error }` with a best-effort message otherwise.
 */
export function validateRecurrenceRule(rule: string): { valid: true } | { valid: false; error: string } {
  if (!rule.trim()) return { valid: false, error: 'RRULE is empty' };
  try {
    const options = RRule.parseString(rule);
    if (options.freq === undefined) return { valid: false, error: 'RRULE is missing FREQ' };
    new RRule(options);
    return { valid: true };
  } catch (e) {
    return { valid: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Produce a human-readable English description of an RRULE string.
 * Returns the original string if parsing fails.
 */
export function describeRecurrenceRule(rule: string): string {
  try {
    const parsed = RRule.parseString(rule);
    const rrule = new RRule(parsed);
    return rrule.toText();
  } catch {
    return rule;
  }
}

/**
 * Parse a raw BYDAY value (e.g. `"1MO"`, `"-1FR"`, `"WE"`) into a
 * {@link PositionalWeekday}. Returns null on invalid input.
 */
export function parsePositionalWeekday(raw: string): PositionalWeekday | null {
  const match = POSITIONAL_WEEKDAY_RE.exec(raw);
  if (!match) return null;
  const day = match[2] as RRuleDay;
  return match[1] ? { day, n: Number(match[1]) } : { day };
}

// ── Internal helpers ───────────────────────────────────────────────────────

function dateKeyToUntilString(dateKey: string): string {
  // UNTIL is represented as UTC date-time. End-of-day keeps it inclusive.
  const m = DATE_KEY_RE.exec(dateKey);
  if (!m) throw new Error(`Invalid UNTIL date: ${dateKey}`);
  return `${m[1]}${m[2]}${m[3]}T235959Z`;
}

function formatPositionalWeekday(pw: PositionalWeekday): string {
  if (pw.n === undefined || pw.n === 0) return pw.day;
  return `${pw.n}${pw.day}`;
}

function normalizeByDay(byweekday: Options['byweekday'] | undefined): PositionalWeekday[] {
  if (byweekday === null || byweekday === undefined) return [];
  const list = Array.isArray(byweekday) ? byweekday : [byweekday];
  return list.map(byweekdayItemToPositional).filter((x): x is PositionalWeekday => x !== null);
}

function byweekdayItemToPositional(item: number | string | Weekday): PositionalWeekday | null {
  if (typeof item === 'number') {
    const day = WEEKDAY_INT_TO_DAY[item];
    return day ? { day } : null;
  }
  if (typeof item === 'string') return parsePositionalWeekday(item);

  const day = WEEKDAY_INT_TO_DAY[item.weekday];
  if (!day) return null;
  return item.n !== undefined && item.n !== 0 ? { day, n: item.n } : { day };
}

function toNumberArray(value: number | number[]): number[] {
  return Array.isArray(value) ? [...value] : [value];
}

/**
 * Expand a recurring slot into individual date occurrences within a visible range.
 *
 * @param slot           - The recurring slot with RRULE
 * @param rangeStart     - Visible range start (YYYY-MM-DD, inclusive)
 * @param rangeEnd       - Visible range end (YYYY-MM-DD, inclusive)
 * @param suppressedDates - Dates to skip (e.g. dates that have an override slot)
 * @returns Array of YYYY-MM-DD date strings where the slot occurs
 */
export function expandRecurrence(
  slot: RecurringSlot,
  rangeStart: string,
  rangeEnd: string,
  suppressedDates?: ReadonlySet<string>
): string[] {
  const dtstart = dateKeyToUtc(slot.dateFrom);
  const parsed = RRule.parseString(slot.recurrenceRule);

  // Apply slot.dateTo as an UNTIL boundary when the rule itself has no UNTIL/COUNT
  if (slot.dateTo && !parsed.until && !parsed.count) {
    parsed.until = dateKeyToUtc(slot.dateTo);
  }

  const rule = new RRule({ ...parsed, dtstart });

  const start = dateKeyToUtc(rangeStart);
  const end = dateKeyToUtc(rangeEnd);

  const occurrences = rule.between(start, end, true);

  const exdateSet = slot.exdates?.length ? new Set(slot.exdates) : undefined;

  const results: string[] = [];
  for (const occ of occurrences) {
    const key = utcToDateKey(occ);
    if (exdateSet?.has(key)) continue;
    if (suppressedDates?.has(key)) continue;
    results.push(key);
  }

  return results;
}
